import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, Loader2, LogOut, ChevronRight, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarDisplay({ value, size = 'sm' }) {
  const cls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <span className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${cls} ${i <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-700 fill-slate-700'}`} />
      ))}
    </span>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending:  { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock,        label: t.reviews.pendingBadge },
    approved: { cls: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle,  label: t.reviews.approvedBadge },
    rejected: { cls: 'bg-red-500/10 text-red-400 border-red-500/20',       icon: XCircle,      label: t.reviews.rejectedBadge },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}

export default function UserAccount() {
  const { t } = useLanguage();
  const { user, loading: authLoading, logout, authHeaders } = useUserAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/user/reviews`, { headers: authHeaders() })
      .then(r => setReviews(r.data))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate, authHeaders]);

  const handleLogout = () => { logout(); navigate('/'); };

  if (authLoading) return (
    <div className="min-h-screen bg-[#020817] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020817] flex flex-col">
      <Navbar />
      <main className="flex-1 py-16 px-4 max-w-3xl mx-auto w-full">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">{t.user.accountTitle}</h1>
            {user && <p className="text-sm text-slate-500 mt-0.5">{user.display_name} · {user.email}</p>}
          </div>
          <button onClick={handleLogout} data-testid="account-logout-btn"
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-400 hover:text-red-400 hover:border-red-500/30 transition-colors">
            <LogOut className="w-4 h-4" /> {t.user.logoutBtn}
          </button>
        </div>

        {/* Reviews */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            {t.user.myReviews}
          </h2>

          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
          ) : reviews.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-slate-500 text-sm">{t.user.noReviews}</p>
              <Link to="/" className="inline-flex items-center gap-1.5 mt-4 text-sm text-cyan-400 hover:text-cyan-300">
                Anbieter entdecken <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.id} data-testid={`my-review-${r.id}`}
                  className="bg-slate-900/40 border border-slate-800/60 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">
                        {t.user.reviewFor}{' '}
                        <Link to={`/anbieter/${r.hosting_id}`}
                          className="text-cyan-400 hover:text-cyan-300 font-medium">
                          {r.hosting_name}
                        </Link>
                      </p>
                      <StarDisplay value={r.stars} />
                      {r.title && <p className="text-sm font-semibold text-white mt-1">{r.title}</p>}
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed">{r.comment}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <StatusBadge status={r.status} t={t} />
                      <span className="text-xs text-slate-600">{r.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-600 pt-1">{t.user.pendingNote}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
